// src/components/PricingModal.js
import { Fragment, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, CheckIcon } from '@heroicons/react/24/outline';
import { Capacitor } from '@capacitor/core';

import DiamondIcon from '../assets/Diamond Default.png';

export default function PricingModal({ isOpen, onClose, user, currentTier = 'free' }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedPlan, setSelectedPlan] = useState('monthly');

  const plans = {
    monthly: {
      name: 'Premium Monthly',
      price: '£5',
      period: 'per month',
      priceId: process.env.REACT_APP_STRIPE_PRICE_MONTHLY,
      savings: null
    },
    annual: {
      name: 'Premium Annual',
      price: '£50',
      period: 'per year',
      priceId: process.env.REACT_APP_STRIPE_PRICE_ANNUAL,
      savings: 'Save £10 per year!'
    }
  };

  const features = [
    'Unlimited AI coach analyses',
    'Full workout history access',
    'Advanced analytics and insights',
    'Unlimited body measurements tracking',
    'Unlimited nutrition history',
    'Personalized recommendations',
    'Export data to PDF/CSV',
    'Priority support',
    'Early access to new features'
  ];

  const handleSubscribe = async (plan) => {
    if (!user) {
      setError('Please log in to subscribe');
      return;
    }

    // Check if price ID exists
    const priceId = plans[plan].priceId;
    console.log('Price ID:', priceId);
    console.log('User ID:', user.id);
    console.log('User Email:', user.email);
    
    if (!priceId) {
      setError(`Price ID not configured for ${plan} plan. Please check your .env file for REACT_APP_STRIPE_PRICE_${plan.toUpperCase()}`);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const requestBody = {
        priceId: priceId,
        userId: user.id,
        email: user.email,
        successUrl: `${window.location.origin}?success=true`,
        cancelUrl: `${window.location.origin}?canceled=true`
      };
      
      console.log('Sending request:', requestBody);

      // Determine the correct API URL based on platform
      const isNative = Capacitor.isNativePlatform();
      const apiUrl = isNative 
        ? 'https://iron-path.com//.netlify/functions/create-checkout-session'
        : '/.netlify/functions/create-checkout-session';

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      console.log('Response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Error response:', errorData);
        throw new Error(errorData.error || 'Failed to create checkout session');
      }

      const data = await response.json();
      console.log('Success response:', data);
      
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (err) {
      console.error('Subscription error:', err);
      setError(err.message || 'Failed to start subscription process. Please try again.');
      setLoading(false);
    }
  };

  const isCurrentPlan = (plan) => {
    if (currentTier === 'free') return false;
    if (plan === 'monthly' && currentTier === 'premium_monthly') return true;
    if (plan === 'annual' && currentTier === 'premium_annual') return true;
    return false;
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-6 text-left align-middle shadow-xl transition-all border border-purple-500/30">
                <div className="flex justify-between items-start mb-6">
                  <Dialog.Title as="h3" className="text-2xl font-bold text-white flex items-center">
                    <img src={DiamondIcon} alt="Diamond" className="w-8 h-8 rounded-md object-cover mr-2 sm:mr-3"/> Upgrade to Premium
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    className="text-purple-300 hover:text-white transition-colors"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                {error && (
                  <div className="mb-4 bg-red-500/20 border border-red-500/50 rounded-lg p-3">
                    <p className="text-red-200 text-sm">{error}</p>
                  </div>
                )}

                {/* Debug Info - Remove in production */}
                {!plans.monthly.priceId || !plans.annual.priceId ? (
                  <div className="mb-4 bg-yellow-500/20 border border-yellow-500/50 rounded-lg p-3">
                    <p className="text-yellow-200 text-sm font-semibold">⚠️ Configuration Issue:</p>
                    <p className="text-yellow-200 text-xs mt-1">
                      Monthly Price ID: {plans.monthly.priceId || 'âŒ Not set'}<br />
                      Annual Price ID: {plans.annual.priceId || 'âŒ Not set'}
                    </p>
                    <p className="text-yellow-200 text-xs mt-2">
                      Make sure your .env file has:<br />
                      REACT_APP_STRIPE_PRICE_MONTHLY=price_xxxxx<br />
                      REACT_APP_STRIPE_PRICE_ANNUAL=price_xxxxx
                    </p>
                  </div>
                ) : null}

                {/* Plan Toggle */}
                <div className="flex justify-center mb-8">
                  <div className="bg-white/10 rounded-lg p-1 inline-flex">
                    <button
                      onClick={() => setSelectedPlan('monthly')}
                      className={`px-6 py-2 rounded-md font-semibold transition-all ${
                        selectedPlan === 'monthly'
                          ? 'bg-purple-600 text-white'
                          : 'text-purple-300 hover:text-white'
                      }`}
                    >
                      Monthly
                    </button>
                    <button
                      onClick={() => setSelectedPlan('annual')}
                      className={`px-6 py-2 rounded-md font-semibold transition-all ${
                        selectedPlan === 'annual'
                          ? 'bg-purple-600 text-white'
                          : 'text-purple-300 hover:text-white'
                      }`}
                    >
                      Annual
                      <span className="ml-2 text-xs bg-green-500/20 text-green-300 px-2 py-1 rounded">
                        Save 17%
                      </span>
                    </button>
                  </div>
                </div>

                {/* Selected Plan Details */}
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Pricing */}
                  <div className="bg-white/10 backdrop-blur-md rounded-xl border border-purple-500/30 p-6">
                    <div className="text-center mb-6">
                      <h4 className="text-xl font-bold text-white mb-2">
                        {plans[selectedPlan].name}
                      </h4>
                      <div className="flex items-baseline justify-center mb-2">
                        <span className="text-5xl font-bold text-white">
                          {plans[selectedPlan].price}
                        </span>
                        <span className="text-purple-300 ml-2">
                          {plans[selectedPlan].period}
                        </span>
                      </div>
                      {plans[selectedPlan].savings && (
                        <p className="text-green-400 text-sm font-medium">
                          {plans[selectedPlan].savings}
                        </p>
                      )}
                    </div>

                    <button
                      onClick={() => handleSubscribe(selectedPlan)}
                      disabled={loading || isCurrentPlan(selectedPlan)}
                      className={`w-full py-3 rounded-lg font-semibold transition-all duration-200 ${
                        isCurrentPlan(selectedPlan)
                          ? 'bg-gray-500 text-gray-300 cursor-not-allowed'
                          : 'bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600 transform hover:scale-105'
                      } disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none`}
                    >
                      {loading ? (
                        <div className="flex items-center justify-center">
                          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                          Processing...
                        </div>
                      ) : isCurrentPlan(selectedPlan) ? (
                        'Current Plan'
                      ) : (
                        'Subscribe Now'
                      )}
                    </button>

                    <p className="text-purple-300 text-xs text-center mt-3">
                      Cancel anytime. No long-term commitment.
                    </p>
                  </div>

                  {/* Features */}
                  <div className="bg-white/10 backdrop-blur-md rounded-xl border border-purple-500/30 p-6">
                    <h5 className="text-lg font-bold text-white mb-4">
                      Everything in Premium:
                    </h5>
                    <ul className="space-y-3">
                      {features.map((feature, index) => (
                        <li key={index} className="flex items-start text-purple-200">
                          <CheckIcon className="h-5 w-5 text-green-400 mr-3 flex-shrink-0 mt-0.5" />
                          <span className="text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Free Tier Comparison */}
                <div className="mt-6 bg-white/5 rounded-lg p-4 border border-white/10">
                  <h5 className="text-sm font-semibold text-purple-300 mb-2">
                    Free Tier Limitations:
                  </h5>
                  <div className="grid sm:grid-cols-3 gap-3 text-xs text-purple-400">
                    <div>• Only 2 AI analyses per month</div>
                    <div>• 30 days workout history</div>
                    <div>• Limited body measurements</div>
                  </div>
                </div>

                {/* Security Badge */}
                <div className="mt-6 flex items-center justify-center text-purple-400 text-xs">
                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Secure payment powered by Stripe
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}